import { useState } from 'react';
import { Role } from '@/types/roles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Pencil, Search, Shield, Users } from 'lucide-react';
import { useRoleUserCount } from '@/hooks/roles';

interface RoleListProps {
  roles: Role[];
  isLoading: boolean;
  onEditRole: (role: Role) => void;
  canManageRoles?: boolean;
}

function RoleUserCountBadge({ roleId }: { roleId: string }) {
  const { data: count, isLoading } = useRoleUserCount(roleId);
  
  if (isLoading) {
    return <Skeleton className="h-5 w-8" />;
  }
  
  return (
    <div className="flex items-center gap-1 text-muted-foreground">
      <Users className="h-3.5 w-3.5" />
      <span className="text-sm">{count || 0}</span>
    </div>
  );
}

export function RoleList({ roles, isLoading, onEditRole, canManageRoles = false }: RoleListProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (roles.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No roles configured yet.</p>
        <p className="text-sm mt-1">Create your first role to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search roles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[100px]">Users</TableHead>
              <TableHead className="w-[100px]">Type</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRoles.map((role) => (
              <TableRow key={role.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {role.name}
                    {role.requires_2fa && (
                      <Badge variant="outline" className="text-xs">
                        2FA
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {role.description || '-'}
                </TableCell>
                <TableCell>
                  <RoleUserCountBadge roleId={role.id} />
                </TableCell>
                <TableCell>
                  {role.is_system ? (
                    <Badge variant="secondary">System</Badge>
                  ) : (
                    <Badge variant="outline">Custom</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {canManageRoles && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditRole(role)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filteredRoles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No roles match your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
